/**
 * Group a flat alarm-row array into a parent → children tree.
 *
 * `getParent(row, index)` returns the parent descriptor (an object
 * with at least `id`; commonly also `name`, `description`,
 * `sortKey`) or `null` when the row should render unparented.
 *
 * Returns indices into the original `alarms` array — Formik
 * FieldArrays track values by index, so callers can render in
 * groups but must never reorder.
 *
 *   {
 *     groups: [{ parent: {id, name, ...}, indices: number[] }],
 *     orphans: number[],
 *   }
 *
 * Groups and child indices are stable-sorted by `parent.sortKey`
 * and `getRowSortKey(row)` respectively when those callbacks are
 * supplied; otherwise insertion order is preserved.
 */
export default function groupAlarmsByParent(alarms, getParent, { getRowSortKey } = {}) {
  const buckets = new Map();
  const orphans = [];

  alarms.forEach((row, index) => {
    const parent = getParent(row, index);
    if (parent == null || parent.id == null) {
      orphans.push(index);
      return;
    }
    if (!buckets.has(parent.id)) {
      buckets.set(parent.id, { parent, indices: [] });
    }
    buckets.get(parent.id).indices.push(index);
  });

  const groups = Array.from(buckets.values());

  groups.sort((a, b) => {
    const ka = a.parent.sortKey;
    const kb = b.parent.sortKey;
    if (ka == null && kb == null) return 0;
    if (ka == null) return 1;
    if (kb == null) return -1;
    return ka - kb;
  });

  if (getRowSortKey) {
    groups.forEach(group => {
      group.indices.sort((a, b) => {
        const ka = getRowSortKey(alarms[a]);
        const kb = getRowSortKey(alarms[b]);
        if (ka == null && kb == null) return 0;
        if (ka == null) return 1;
        if (kb == null) return -1;
        return ka - kb;
      });
    });
  }

  return { groups, orphans };
}
